import {
  DotsThreeIcon,
  ExportIcon,
  FloppyDiskIcon,
} from "@phosphor-icons/react";
import IconButton from "@/components/ui/icon-button";
import { Menu, MenuPopover, MenuTrigger } from "@/components/ui/menu";
import { CustomMenuItem, type MenuItemConfig } from "@/components/ui/menu-item";
import { toast } from "@/components/ui/toast";
import { ExportFeedArticlesInCSVFormat } from "@/data-access/news-feeds/queries/news-feeds-queries";
import { useCsvExport } from "@/lib/hooks/useCsvExport";
import { useFeedName } from "@/store/news-feeds/news-feeds.selectors";
import useSavedViewStore from "@/store/saved-views-v2/saved-views-management.slice";
import { useExportFeedArticlesInput } from "./useExportFeedArticlesInput";

export interface NewsFeedControlsMoreOptionsParams {
  feedId: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  filterDays: number | undefined;
  formattedStartDate: string;
  formattedEndDate: string;
}

/**
 * More options menu (Save as view, Export). Router-agnostic; parent supplies feed/date params.
 */
const NewsFeedControlsMoreOptions = ({
  feedId,
  filterDays,
  formattedStartDate,
  formattedEndDate,
}: NewsFeedControlsMoreOptionsParams) => {
  const feedName = useFeedName(feedId);
  const { input } = useExportFeedArticlesInput();
  const { triggerExport, DownloadAnchor } = useCsvExport({
    mutation: ExportFeedArticlesInCSVFormat,
    getResponses: (data) => data.exportFeedArticleProjectionsCsv ?? [],
    getFilename: () => `${feedName}-${new Date().toISOString()}-export.csv`,
  });

  const setIsCreateOrEditSavedViewModalOpen = useSavedViewStore(
    (state) => state.setIsCreateOrEditSavedViewModalOpen,
  );
  const setSavedViewName = useSavedViewStore((state) => state.setSavedViewName);
  const resetCreateEditSavedView = useSavedViewStore(
    (state) => state.resetCreateEditSavedView,
  );

  const handleExport = () => {
    if (!input) {
      toast.error("Select a date range to export");
      return;
    }
    triggerExport({ input });
  };

  const handleSaveAsView = () => {
    resetCreateEditSavedView();

    let viewName: string;
    if (filterDays) {
      viewName = `My News Feed - [last ${filterDays} days]`;
    } else {
      viewName = `My News Feed - [${formattedStartDate} - ${formattedEndDate}]`;
    }

    setSavedViewName(viewName);
    setIsCreateOrEditSavedViewModalOpen(true);
  };

  const menuItems: MenuItemConfig[] = [
    {
      id: "save-as-view",
      icon: <FloppyDiskIcon size={16} />,
      label: "Save as view",
      onClick: handleSaveAsView,
    },
    {
      id: "export",
      icon: <ExportIcon size={16} />,
      label: "Export",
      onClick: handleExport,
    },
  ];

  return (
    <>
      <MenuTrigger>
        <IconButton
          icon={<DotsThreeIcon />}
          variant="ghost"
          size="md"
          aria-label="More Options"
          className="text-gray-600"
          tooltipLabel="More Options"
        />
        <MenuPopover className="w-48">
          <Menu>
            {menuItems.map((item) => (
              <CustomMenuItem key={String(item.label)} config={item} />
            ))}
          </Menu>
        </MenuPopover>
      </MenuTrigger>

      {DownloadAnchor}
    </>
  );
};

export default NewsFeedControlsMoreOptions;
